/**
 * Outlook Calendar OAuth Callback.
 *
 * Runs in a popup (window.name === 'outlook-auth') or as a redirect fallback.
 *
 * Popup flow: writes code to localStorage → parent's storage event fires → parent exchanges token → popup closes.
 * Redirect flow: exchanges token directly, navigates back to schedule.
 */
import React, { useEffect, useState } from 'react';
import { IonPage, IonContent, IonSpinner, IonText } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

const OutlookCalendarCallback: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    // ── Popup flow — should have been redirected to /outlook-callback.html by index.html.
    // If React loaded here anyway, close the popup.
    if (params.get('state') === 'popup') {
      window.close();
      return;
    }

    Promise.resolve().then(() => {
      // ── Redirect flow (native / popup blocked) ─────────────────────────
      if (error) {
        setErrorMsg(error === 'access_denied' ? t('pages.OutlookCalendarCallback.accessWasDenied') : error);
        setTimeout(() => router.push('/tabs/schedule', 'back'), 2500);
        return;
      }
      if (!code) {
        setErrorMsg(t('pages.OutlookCalendarCallback.noAuthorizationCodeReceived'));
        setTimeout(() => router.push('/tabs/schedule', 'back'), 2500);
        return;
      }
      const codeVerifier = localStorage.getItem('outlookCodeVerifier');
      localStorage.removeItem('outlookCodeVerifier');
      if (!codeVerifier) {
        setErrorMsg(t('pages.OutlookCalendarCallback.pkceVerifierMissingPleaseTryConnecting'));
        setTimeout(() => router.push('/tabs/schedule', 'back'), 2500);
        return;
      }
      const fn = httpsCallable<{ action: string; [k: string]: unknown }, { email: string }>(getFunctions(), 'outlookCalendar');
      fn({ action: 'saveTokens', code, codeVerifier, redirectUri: `${window.location.origin}/outlook-calendar-callback` })
        .then(() => router.push('/tabs/schedule', 'back'))
        .catch((err) => {
          setErrorMsg(err?.message ?? t('pages.OutlookCalendarCallback.couldNotConnectOutlookPleaseTry'));
          setTimeout(() => router.push('/tabs/schedule', 'back'), 2500);
        });
    });
  }, [router, t]);

  return (
    <IonPage>
      <IonContent>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
          {errorMsg
            ? <IonText color="danger">{errorMsg}</IonText>
            : <><IonSpinner name="bubbles" color="primary" /><IonText>{t('pages.OutlookCalendarCallback.connectingYourOutlookCalendar')}</IonText></>}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default OutlookCalendarCallback;
